import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { listUsers, AdminUser } from "../../services/adminService";
import { listOrders, AdminOrder } from "../../services/adminOrdersService";
import { fetchProducts, Product } from "../../services/productService";
import {
  createCustomerCrmTask,
  CustomerCRMProfile,
  CustomerCRMTask,
  getCustomerCrmProfile,
  listCustomerCrmTasks,
  upsertCustomerCrmProfile,
  updateCustomerCrmTask,
} from "../../services/customerCrmService";

type CustomerCRMTab = "directory" | "segments" | "followups";
type StatusFilter = "all" | "active" | "inactive";
type SegmentFilter = "all" | "vip" | "repeat" | "new" | "at_risk" | "dormant";

type CustomerMetrics = {
  customerId: string;
  orderCount: number;
  totalSpend: number;
  totalQuantity: number;
  avgOrderValue: number;
  deliveredOrders: number;
  pendingOrders: number;
  lastOrderAt: string | null;
  firstOrderAt: string | null;
  topProducts: Array<{ productId: string; count: number; amount: number }>;
  recentOrders: AdminOrder[];
};

const currency = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  maximumFractionDigits: 0,
});

const numberFmt = new Intl.NumberFormat("en-NG");

const formatCurrency = (value: number) => currency.format(Number.isFinite(value) ? value : 0);
const formatNumber = (value: number) => numberFmt.format(Number.isFinite(value) ? value : 0);

const parseJson = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const getTenantId = () => {
  const tenant = parseJson<{ id?: string }>(localStorage.getItem("currentTenant"));
  const userData = parseJson<{ tenant_id?: string }>(localStorage.getItem("user_data"));
  const user = parseJson<{ tenant_id?: string }>(localStorage.getItem("user"));
  return tenant?.id || userData?.tenant_id || user?.tenant_id || "";
};

const formatDate = (value?: string | null) => {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleString();
};

const daysSince = (value?: string | null) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
};

const getLifecycle = (customer: AdminUser, metrics: CustomerMetrics | undefined) => {
  if (customer.is_active === false) return "inactive";
  if (!metrics || metrics.orderCount === 0) return "new";
  const lastDays = daysSince(metrics.lastOrderAt);
  if (lastDays == null) return "new";
  if (lastDays > 90) return "dormant";
  if (lastDays > 30) return "at_risk";
  return "active";
};

const getSegment = (customer: AdminUser, metrics: CustomerMetrics | undefined): SegmentFilter => {
  const lifecycle = getLifecycle(customer, metrics);
  if (lifecycle === "dormant") return "dormant";
  if (lifecycle === "at_risk") return "at_risk";
  if (!metrics || metrics.orderCount === 0) return "new";
  if (metrics.totalSpend >= 500_000 || metrics.orderCount >= 8) return "vip";
  if (metrics.orderCount >= 2) return "repeat";
  return "new";
};

const lifecycleBadge = (lifecycle: string) => {
  switch (lifecycle) {
    case "active":
      return "bg-emerald-100 text-emerald-700";
    case "at_risk":
      return "bg-amber-100 text-amber-700";
    case "dormant":
      return "bg-rose-100 text-rose-700";
    case "inactive":
      return "bg-gray-200 text-gray-700";
    default:
      return "bg-blue-100 text-blue-700";
  }
};

const segmentBadge = (segment: SegmentFilter) => {
  switch (segment) {
    case "vip":
      return "bg-purple-100 text-purple-700";
    case "repeat":
      return "bg-blue-100 text-blue-700";
    case "at_risk":
      return "bg-amber-100 text-amber-700";
    case "dormant":
      return "bg-rose-100 text-rose-700";
    case "new":
      return "bg-green-100 text-green-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const segmentLabel: Record<SegmentFilter, string> = {
  all: "All",
  vip: "VIP",
  repeat: "Repeat",
  new: "New",
  at_risk: "At Risk",
  dormant: "Dormant",
};

const crmTabs: Array<{ key: CustomerCRMTab; label: string }> = [
  { key: "directory", label: "Customer Directory" },
  { key: "segments", label: "Segments & Value" },
  { key: "followups", label: "Follow-up Queue" },
];

const AdminCustomers: React.FC = () => {
  const [customers, setCustomers] = useState<AdminUser[]>([]);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [segmentFilter, setSegmentFilter] = useState<SegmentFilter>("all");
  const [activeTab, setActiveTab] = useState<CustomerCRMTab>("directory");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [crmProfilesByCustomer, setCrmProfilesByCustomer] = useState<Record<string, CustomerCRMProfile | null>>({});
  const [crmTasksByCustomer, setCrmTasksByCustomer] = useState<Record<string, CustomerCRMTask[]>>({});
  const [notesDraftByCustomer, setNotesDraftByCustomer] = useState<Record<string, string>>({});
  const [crmDetailLoadingByCustomer, setCrmDetailLoadingByCustomer] = useState<Record<string, boolean>>({});

  const tenantId = useMemo(() => getTenantId(), []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [customerData, orderData, productData] = await Promise.allSettled([
        listUsers("customer"),
        listOrders(),
        tenantId ? fetchProducts(tenantId, { limit: 500, sort_by: "created_at", sort_order: "desc" }) : Promise.resolve([]),
      ]);

      if (customerData.status === "fulfilled") setCustomers(customerData.value || []);
      else throw customerData.reason;

      if (orderData.status === "fulfilled") setOrders(orderData.value || []);
      else setOrders([]);

      if (productData.status === "fulfilled") setProducts(productData.value || []);
      else setProducts([]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to load customer CRM data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const productMap = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products]
  );

  const customerMetricsMap = useMemo(() => {
    const grouped = new Map<string, AdminOrder[]>();
    for (const order of orders) {
      const customerId = order.customer_id;
      if (!customerId) continue;
      if (!grouped.has(customerId)) grouped.set(customerId, []);
      grouped.get(customerId)!.push(order);
    }

    const result: Record<string, CustomerMetrics> = {};
    for (const [customerId, customerOrders] of grouped.entries()) {
      const sorted = [...customerOrders].sort((a, b) => {
        const ad = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bd = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bd - ad;
      });
      const totalSpend = customerOrders.reduce((sum, o) => sum + Number(o.total_price || 0), 0);
      const totalQuantity = customerOrders.reduce((sum, o) => sum + Number(o.quantity || 0), 0);
      const deliveredOrders = customerOrders.filter((o) => (o.status || "").toLowerCase() === "delivered").length;
      const pendingOrders = customerOrders.filter((o) =>
        ["pending", "processing", "shipped"].includes((o.status || "").toLowerCase())
      ).length;
      const productSpend = new Map<string, { count: number; amount: number }>();
      for (const o of customerOrders) {
        const key = o.product_id || "unknown";
        const current = productSpend.get(key) || { count: 0, amount: 0 };
        current.count += 1;
        current.amount += Number(o.total_price || 0);
        productSpend.set(key, current);
      }

      result[customerId] = {
        customerId,
        orderCount: customerOrders.length,
        totalSpend,
        totalQuantity,
        avgOrderValue: customerOrders.length ? totalSpend / customerOrders.length : 0,
        deliveredOrders,
        pendingOrders,
        lastOrderAt: sorted[0]?.created_at || null,
        firstOrderAt: sorted[sorted.length - 1]?.created_at || null,
        topProducts: Array.from(productSpend.entries())
          .map(([productId, stats]) => ({ productId, ...stats }))
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 5),
        recentOrders: sorted.slice(0, 6),
      };
    }
    return result;
  }, [orders]);

  const enrichedCustomers = useMemo(() => {
    return customers.map((customer) => {
      const metrics = customerMetricsMap[customer.id];
      const lifecycle = getLifecycle(customer, metrics);
      const segment = getSegment(customer, metrics);
      return {
        ...customer,
        metrics,
        lifecycle,
        segment,
        displayName: customer.full_name || customer.name || "Unnamed Customer",
      };
    });
  }, [customers, customerMetricsMap]);

  const filteredCustomers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return enrichedCustomers.filter((customer) => {
      const matchesSearch =
        !query ||
        customer.displayName.toLowerCase().includes(query) ||
        (customer.email || "").toLowerCase().includes(query) ||
        (customer.tenant_id || "").toLowerCase().includes(query) ||
        (customer.id || "").toLowerCase().includes(query);

      const isActive = customer.is_active !== false;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && isActive) ||
        (statusFilter === "inactive" && !isActive);

      const matchesSegment = segmentFilter === "all" || customer.segment === segmentFilter;

      return matchesSearch && matchesStatus && matchesSegment;
    });
  }, [enrichedCustomers, search, statusFilter, segmentFilter]);

  const selectedCustomer =
    enrichedCustomers.find((c) => c.id === selectedCustomerId) ||
    filteredCustomers[0] ||
    null;

  const loadCustomerCrmDetail = async (customerId: string) => {
    setCrmDetailLoadingByCustomer((prev) => ({ ...prev, [customerId]: true }));
    try {
      const [profileRes, tasksRes] = await Promise.all([
        getCustomerCrmProfile(customerId),
        listCustomerCrmTasks(customerId),
      ]);
      setCrmProfilesByCustomer((prev) => ({ ...prev, [customerId]: profileRes.profile || null }));
      setCrmTasksByCustomer((prev) => ({ ...prev, [customerId]: tasksRes.tasks || [] }));
      setNotesDraftByCustomer((prev) => ({
        ...prev,
        [customerId]:
          prev[customerId] !== undefined ? prev[customerId] : (profileRes.profile?.notes as string) || "",
      }));
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to load CRM profile");
    } finally {
      setCrmDetailLoadingByCustomer((prev) => ({ ...prev, [customerId]: false }));
    }
  };

  useEffect(() => {
    if (!selectedCustomerId && filteredCustomers.length > 0) {
      setSelectedCustomerId(filteredCustomers[0].id);
      return;
    }
    if (selectedCustomerId && !filteredCustomers.some((c) => c.id === selectedCustomerId)) {
      setSelectedCustomerId(filteredCustomers[0]?.id || null);
    }
  }, [filteredCustomers, selectedCustomerId]);

  useEffect(() => {
    if (!selectedCustomer?.id) return;
    if (crmProfilesByCustomer[selectedCustomer.id] !== undefined && crmTasksByCustomer[selectedCustomer.id] !== undefined) return;
    void loadCustomerCrmDetail(selectedCustomer.id);
  }, [selectedCustomer?.id]);

  const totals = useMemo(() => {
    const totalCustomers = enrichedCustomers.length;
    const activeCustomers = enrichedCustomers.filter((c) => c.is_active !== false).length;
    const inactiveCustomers = enrichedCustomers.filter((c) => c.is_active === false).length;
    const vipCustomers = enrichedCustomers.filter((c) => c.segment === "vip").length;
    const repeatCustomers = enrichedCustomers.filter((c) => c.segment === "repeat").length;
    const atRiskCustomers = enrichedCustomers.filter((c) => c.segment === "at_risk").length;
    const dormantCustomers = enrichedCustomers.filter((c) => c.segment === "dormant").length;
    const totalSpend = enrichedCustomers.reduce((sum, c) => sum + (c.metrics?.totalSpend || 0), 0);
    const totalOrders = enrichedCustomers.reduce((sum, c) => sum + (c.metrics?.orderCount || 0), 0);
    return {
      totalCustomers,
      activeCustomers,
      inactiveCustomers,
      vipCustomers,
      repeatCustomers,
      atRiskCustomers,
      dormantCustomers,
      totalSpend,
      totalOrders,
    };
  }, [enrichedCustomers]);

  const followUpQueue = useMemo(() => {
    return enrichedCustomers
      .filter((c) => c.segment === "at_risk" || c.segment === "dormant" || c.segment === "vip")
      .sort((a, b) => {
        const priority = (customer: (typeof enrichedCustomers)[number]) => {
          if (customer.segment === "dormant") return 3;
          if (customer.segment === "at_risk") return 2;
          if (customer.segment === "vip") return 1;
          return 0;
        };
        return priority(b) - priority(a) || (b.metrics?.totalSpend || 0) - (a.metrics?.totalSpend || 0);
      });
  }, [enrichedCustomers]);

  const saveSelectedCustomerNote = async () => {
    if (!selectedCustomer) return;
    try {
      const notes = notesDraftByCustomer[selectedCustomer.id] || "";
      const result = await upsertCustomerCrmProfile(selectedCustomer.id, { notes });
      setCrmProfilesByCustomer((prev) => ({ ...prev, [selectedCustomer.id]: result.profile }));
      toast.success("CRM note saved to database");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to save CRM note");
    }
  };

  const createFollowUpForCustomer = async (
    customerId: string,
    title: string,
    description?: string,
    priority: "low" | "medium" | "high" | "urgent" = "medium"
  ) => {
    try {
      const result = await createCustomerCrmTask(customerId, {
        title,
        description,
        priority,
        status: "open",
      });
      setCrmTasksByCustomer((prev) => ({
        ...prev,
        [customerId]: [result.task, ...(prev[customerId] || [])],
      }));
      toast.success("Follow-up task saved to database");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to create follow-up task");
    }
  };

  const updateTaskStatus = async (customerId: string, taskId: string, status: string) => {
    try {
      const result = await updateCustomerCrmTask(taskId, { status });
      setCrmTasksByCustomer((prev) => ({
        ...prev,
        [customerId]: (prev[customerId] || []).map((t) => (t.id === taskId ? result.task : t)),
      }));
      toast.success(`Task marked ${status}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to update task");
    }
  };

  const segmentSummary = useMemo(() => {
    const rows: Array<{ key: SegmentFilter; count: number; revenue: number; label: string }> = [
      "vip",
      "repeat",
      "new",
      "at_risk",
      "dormant",
    ].map((segment) => {
      const customersInSegment = enrichedCustomers.filter((c) => c.segment === segment);
      return {
        key: segment as SegmentFilter,
        label: segmentLabel[segment as SegmentFilter],
        count: customersInSegment.length,
        revenue: customersInSegment.reduce((sum, c) => sum + (c.metrics?.totalSpend || 0), 0),
      };
    });
    return rows;
  }, [enrichedCustomers]);

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Customer CRM</h1>
            <p className="mt-2 text-sm text-gray-600">
              Tenant-admin relationship management for buyers: directory, spend, order activity, segments, and follow-up actions.
            </p>
          </div>
          <div className="text-sm text-gray-600">
            <div>
              <span className="font-medium text-gray-800">Tenant:</span>{" "}
              <span className="font-mono text-xs">{tenantId || "Not detected"}</span>
            </div>
            <div className="mt-1">
              <span className="font-medium text-gray-800">Sources:</span> users + orders (+ products for labels)
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Customers</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(totals.totalCustomers)}</div>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">CRM Revenue</div>
            <div className="mt-1 text-xl font-bold text-emerald-800">{formatCurrency(totals.totalSpend)}</div>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Orders</div>
            <div className="mt-1 text-2xl font-bold text-blue-800">{formatNumber(totals.totalOrders)}</div>
          </div>
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">VIP</div>
            <div className="mt-1 text-2xl font-bold text-purple-800">{formatNumber(totals.vipCustomers)}</div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">At Risk</div>
            <div className="mt-1 text-2xl font-bold text-amber-800">{formatNumber(totals.atRiskCustomers)}</div>
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-rose-700">Dormant</div>
            <div className="mt-1 text-2xl font-bold text-rose-800">{formatNumber(totals.dormantCustomers)}</div>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-3 md:flex-row">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by customer name, email, tenant, or ID..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
            >
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
            <select
              value={segmentFilter}
              onChange={(e) => setSegmentFilter(e.target.value as SegmentFilter)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
            >
              {Object.entries(segmentLabel).map(([value, label]) => (
                <option key={value} value={value}>
                  {label} segment
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setSearch("");
                setStatusFilter("all");
                setSegmentFilter("all");
              }}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={() => {
                void load();
                toast.success("Refreshing customer CRM...");
              }}
              className="rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {crmTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                activeTab === tab.key
                  ? "bg-green-700 text-white"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-600 shadow-sm">
          Loading customer CRM data...
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 shadow-sm">
          {error}
        </div>
      )}

      {!loading && !error && customers.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">No customers found yet</h2>
          <p className="mt-2 text-sm text-gray-600">
            This tenant admin CRM will activate when customer accounts and customer orders exist under this tenant.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">Directory</div>
              <div className="mt-2 text-xs text-gray-600">Customer accounts, status, tenant mapping, and account records.</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">CRM Metrics</div>
              <div className="mt-2 text-xs text-gray-600">Order count, spend, last order date, lifecycle and segment classification.</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">Follow-up Queue</div>
              <div className="mt-2 text-xs text-gray-600">At-risk, dormant, and VIP customer outreach actions for the tenant admin.</div>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && customers.length > 0 && (
        <div className="space-y-6">
          {activeTab === "directory" && (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="xl:col-span-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Customer CRM Directory</h2>
                  <div className="text-xs text-gray-500">
                    Showing {filteredCustomers.length} of {enrichedCustomers.length}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Customer</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Segment</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Lifecycle</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Orders</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Total Spend</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Last Order</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCustomers.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                            No customers match current filters.
                          </td>
                        </tr>
                      ) : (
                        filteredCustomers.map((c) => (
                          <tr
                            key={c.id}
                            className={`border-b border-gray-100 last:border-none cursor-pointer hover:bg-gray-50 ${
                              selectedCustomer?.id === c.id ? "bg-green-50/60" : ""
                            }`}
                            onClick={() => setSelectedCustomerId(c.id)}
                          >
                            <td className="px-3 py-3">
                              <div className="font-medium text-gray-900">{c.displayName}</div>
                              <div className="text-xs text-gray-500">{c.email}</div>
                              <div className="mt-1 text-[11px] font-mono text-gray-400">{c.id}</div>
                            </td>
                            <td className="px-3 py-3">
                              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${segmentBadge(c.segment)}`}>
                                {segmentLabel[c.segment]}
                              </span>
                            </td>
                            <td className="px-3 py-3">
                              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${lifecycleBadge(c.lifecycle)}`}>
                                {c.lifecycle.replace("_", " ")}
                              </span>
                              <div className="mt-1 text-xs text-gray-500">
                                {c.is_active === false ? "Account inactive" : "Account active"}
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-sm font-semibold text-gray-900">{formatNumber(c.metrics?.orderCount || 0)}</div>
                              <div className="text-xs text-gray-500">
                                Pending: {formatNumber(c.metrics?.pendingOrders || 0)}
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-sm font-semibold text-gray-900">
                                {formatCurrency(c.metrics?.totalSpend || 0)}
                              </div>
                              <div className="text-xs text-gray-500">
                                AOV: {formatCurrency(c.metrics?.avgOrderValue || 0)}
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-sm text-gray-900">{formatDate(c.metrics?.lastOrderAt)}</div>
                              <div className="text-xs text-gray-500">
                                {c.metrics?.lastOrderAt ? `${daysSince(c.metrics.lastOrderAt) ?? 0}d ago` : "No orders"}
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCustomerId(c.id);
                                    toast.success(`Opened CRM profile for ${c.displayName}`);
                                  }}
                                  className="text-left text-xs font-medium text-green-700 hover:underline"
                                >
                                  Open CRM
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void createFollowUpForCustomer(
                                      c.id,
                                      `Follow up ${c.segment === "vip" ? "VIP" : "customer"} account`,
                                      c.segment === "dormant"
                                        ? "Win-back outreach for dormant buyer"
                                        : c.segment === "at_risk"
                                        ? "Re-engagement check-in due to inactivity"
                                        : "Customer relationship follow-up",
                                      c.segment === "vip" ? "high" : c.segment === "dormant" ? "high" : "medium"
                                    );
                                  }}
                                  className="text-left text-xs font-medium text-blue-700 hover:underline"
                                >
                                  Add Follow-up
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-6">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-900">Customer CRM Profile</h2>
                  {!selectedCustomer ? (
                    <div className="mt-3 text-sm text-gray-500">Select a customer to view CRM details.</div>
                  ) : (
                    <div className="mt-4 space-y-4">
                      <div>
                        <div className="text-base font-semibold text-gray-900">{selectedCustomer.displayName}</div>
                        <div className="text-sm text-gray-600">{selectedCustomer.email}</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className={`rounded-full px-2 py-1 text-xs font-semibold ${segmentBadge(selectedCustomer.segment)}`}>
                            {segmentLabel[selectedCustomer.segment]}
                          </span>
                          <span className={`rounded-full px-2 py-1 text-xs font-semibold ${lifecycleBadge(selectedCustomer.lifecycle)}`}>
                            {selectedCustomer.lifecycle.replace("_", " ")}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="text-xs text-gray-500">Orders</div>
                          <div className="text-lg font-bold text-gray-900">{formatNumber(selectedCustomer.metrics?.orderCount || 0)}</div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="text-xs text-gray-500">Total Spend</div>
                          <div className="text-lg font-bold text-gray-900">{formatCurrency(selectedCustomer.metrics?.totalSpend || 0)}</div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="text-xs text-gray-500">AOV</div>
                          <div className="text-lg font-bold text-gray-900">{formatCurrency(selectedCustomer.metrics?.avgOrderValue || 0)}</div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="text-xs text-gray-500">Last Order</div>
                          <div className="text-sm font-semibold text-gray-900">
                            {selectedCustomer.metrics?.lastOrderAt
                              ? `${daysSince(selectedCustomer.metrics.lastOrderAt) ?? 0}d ago`
                              : "No orders"}
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-2 text-sm font-semibold text-gray-900">Top Purchased Products</div>
                        <div className="space-y-2">
                          {(selectedCustomer.metrics?.topProducts || []).length === 0 ? (
                            <div className="text-xs text-gray-500">No product purchases yet.</div>
                          ) : (
                            selectedCustomer.metrics!.topProducts.map((p) => (
                              <div key={p.productId} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {productMap[p.productId]?.name || `Product ${p.productId.slice(0, 8)}`}
                                </div>
                                <div className="mt-1 text-xs text-gray-600">
                                  {formatNumber(p.count)} orders • {formatCurrency(p.amount)}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      <div>
                        <div className="mb-2 text-sm font-semibold text-gray-900">
                          CRM Notes (Database)
                          {crmDetailLoadingByCustomer[selectedCustomer.id] ? (
                            <span className="ml-2 text-xs text-gray-500">Loading...</span>
                          ) : null}
                        </div>
                        <textarea
                          value={notesDraftByCustomer[selectedCustomer.id] || ""}
                          onChange={(e) =>
                            setNotesDraftByCustomer((prev) => ({ ...prev, [selectedCustomer.id]: e.target.value }))
                          }
                          rows={4}
                          placeholder="Add follow-up notes, negotiation terms, preferences, payment behavior..."
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
                        />
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={() => void saveSelectedCustomerNote()}
                            className="rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700"
                          >
                            Save Note
                          </button>
                          <button
                            onClick={() =>
                              setNotesDraftByCustomer((prev) => ({ ...prev, [selectedCustomer.id]: "" }))
                            }
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Clear Note
                          </button>
                        </div>
                      </div>

                      <div>
                        <div className="mb-2 text-sm font-semibold text-gray-900">CRM Follow-up Tasks (Database)</div>
                        <div className="space-y-2">
                          {(crmTasksByCustomer[selectedCustomer.id] || []).length === 0 ? (
                            <div className="text-xs text-gray-500">No CRM tasks created yet for this customer.</div>
                          ) : (
                            (crmTasksByCustomer[selectedCustomer.id] || []).slice(0, 6).map((task) => (
                              <div key={task.id} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{task.title}</div>
                                    {task.description && (
                                      <div className="mt-1 text-xs text-gray-600">{task.description}</div>
                                    )}
                                  </div>
                                  <span
                                    className={`rounded-full px-2 py-1 text-xs font-semibold ${
                                      task.status === "done"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : task.status === "in_progress"
                                        ? "bg-blue-100 text-blue-700"
                                        : "bg-amber-100 text-amber-700"
                                    }`}
                                  >
                                    {task.status}
                                  </span>
                                </div>
                                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                                  <span>Priority: {task.priority}</span>
                                  <span>{task.due_at ? `Due ${formatDate(task.due_at)}` : "No due date"}</span>
                                </div>
                                <div className="mt-2 flex gap-2">
                                  {task.status !== "done" && (
                                    <button
                                      onClick={() => void updateTaskStatus(selectedCustomer.id, task.id, "done")}
                                      className="text-xs font-medium text-green-700 hover:underline"
                                    >
                                      Mark done
                                    </button>
                                  )}
                                  {task.status === "open" && (
                                    <button
                                      onClick={() => void updateTaskStatus(selectedCustomer.id, task.id, "in_progress")}
                                      className="text-xs font-medium text-blue-700 hover:underline"
                                    >
                                      Start
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-900">Relationship Playbook</h2>
                  <ul className="mt-3 space-y-2 text-sm text-gray-600">
                    <li>VIP: prioritize direct outreach, negotiated pricing, and stock reservation.</li>
                    <li>At Risk: trigger reactivation contact if no orders in 30+ days.</li>
                    <li>Dormant: run win-back offers and verify account status/contact channel.</li>
                    <li>New: onboarding support, first-order incentives, and product recommendations.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === "segments" && (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="xl:col-span-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">Segment Value Summary</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Segment</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Customers</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Revenue</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Share of CRM Revenue</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Recommended Focus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {segmentSummary.map((row) => (
                        <tr key={row.key} className="border-b border-gray-100 last:border-none">
                          <td className="px-4 py-3">
                            <span className={`rounded-full px-2 py-1 text-xs font-semibold ${segmentBadge(row.key)}`}>
                              {row.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">{formatNumber(row.count)}</td>
                          <td className="px-4 py-3">{formatCurrency(row.revenue)}</td>
                          <td className="px-4 py-3">
                            {totals.totalSpend ? `${((row.revenue / totals.totalSpend) * 100).toFixed(1)}%` : "0.0%"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {row.key === "vip"
                              ? "Protect accounts, allocate stock, premium support"
                              : row.key === "repeat"
                              ? "Upsell/cross-sell and frequency programs"
                              : row.key === "new"
                              ? "Onboard and convert to second order"
                              : row.key === "at_risk"
                              ? "Re-engagement and issue-resolution outreach"
                              : "Win-back campaign and relationship reset"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-6">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-900">Top Customers by Spend</h2>
                  <div className="mt-3 space-y-3">
                    {[...enrichedCustomers]
                      .sort((a, b) => (b.metrics?.totalSpend || 0) - (a.metrics?.totalSpend || 0))
                      .slice(0, 8)
                      .map((c) => (
                        <div
                          key={c.id}
                          className="rounded-lg border border-gray-100 bg-gray-50 p-3 cursor-pointer hover:bg-gray-100"
                          onClick={() => {
                            setSelectedCustomerId(c.id);
                            setActiveTab("directory");
                          }}
                        >
                          <div className="text-sm font-semibold text-gray-900">{c.displayName}</div>
                          <div className="mt-1 text-xs text-gray-600">
                            {formatCurrency(c.metrics?.totalSpend || 0)} • {formatNumber(c.metrics?.orderCount || 0)} orders
                          </div>
                          <div className="mt-2">
                            <span className={`rounded-full px-2 py-1 text-xs font-semibold ${segmentBadge(c.segment)}`}>
                              {segmentLabel[c.segment]}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-900">Segment Targets</h2>
                  <ul className="mt-3 space-y-2 text-sm text-gray-600">
                    <li><span className="font-medium text-gray-800">VIP:</span> account plans, priority logistics, negotiated terms.</li>
                    <li><span className="font-medium text-gray-800">Repeat:</span> bundle offers, subscription/recurring orders.</li>
                    <li><span className="font-medium text-gray-800">New:</span> onboarding call, category recommendations.</li>
                    <li><span className="font-medium text-gray-800">At Risk:</span> check complaints, pricing/stock issues.</li>
                    <li><span className="font-medium text-gray-800">Dormant:</span> win-back campaign and direct contact.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === "followups" && (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="xl:col-span-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Follow-up Queue</h2>
                  <div className="text-xs text-gray-500">
                    Prioritized: Dormant → At Risk → VIP
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Customer</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Segment</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Last Order</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Value</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Suggested Action</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">CRM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {followUpQueue.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                            No follow-up candidates detected yet.
                          </td>
                        </tr>
                      ) : (
                        followUpQueue.map((c) => {
                          const action =
                            c.segment === "dormant"
                              ? "Win-back outreach + promo offer"
                              : c.segment === "at_risk"
                              ? "Check inactivity cause, pricing, service issue"
                              : "VIP relationship check-in / reserved stock update";
                          return (
                            <tr key={c.id} className="border-b border-gray-100 last:border-none">
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">{c.displayName}</div>
                                <div className="text-xs text-gray-500">{c.email}</div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${segmentBadge(c.segment)}`}>
                                  {segmentLabel[c.segment]}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-gray-900">{formatDate(c.metrics?.lastOrderAt)}</div>
                                <div className="text-xs text-gray-500">
                                  {c.metrics?.lastOrderAt ? `${daysSince(c.metrics.lastOrderAt) ?? 0}d ago` : "No orders"}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-semibold text-gray-900">{formatCurrency(c.metrics?.totalSpend || 0)}</div>
                                <div className="text-xs text-gray-500">{formatNumber(c.metrics?.orderCount || 0)} orders</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{action}</td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => {
                                    setSelectedCustomerId(c.id);
                                    setActiveTab("directory");
                                    toast.success(`Open CRM profile for ${c.displayName}`);
                                  }}
                                  className="text-xs font-medium text-green-700 hover:underline"
                                >
                                  Open Profile
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-6">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-900">Queue Metrics</h2>
                  <div className="mt-3 grid grid-cols-1 gap-3">
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">At Risk</div>
                      <div className="text-xl font-bold text-amber-800">{formatNumber(totals.atRiskCustomers)}</div>
                    </div>
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-rose-700">Dormant</div>
                      <div className="text-xl font-bold text-rose-800">{formatNumber(totals.dormantCustomers)}</div>
                    </div>
                    <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">VIP</div>
                      <div className="text-xl font-bold text-purple-800">{formatNumber(totals.vipCustomers)}</div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-900">How the tenant admin uses this</h2>
                  <ol className="mt-3 space-y-3 text-sm text-gray-600">
                    <li>1. Review at-risk and dormant buyers in the follow-up queue.</li>
                    <li>2. Open a customer profile, check spend/order history and top products.</li>
                    <li>3. Record outreach notes and next steps in CRM Notes.</li>
                    <li>4. Prioritize VIP buyers for stock allocation and relationship calls.</li>
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
